import {
  NOT_PULLING,
  PULL_THRESHOLD,
  onTouchMove,
  onTouchStart,
  pullProgress,
  shouldRefresh,
  type PullState,
} from '@agos/customer-core'
import { tt } from '@agos/shared'
import { Loader2, RotateCw } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Pull down at the top of the page to reload it — the gesture half.
 *
 * The arithmetic is in `@agos/customer-core` and tested without a touchscreen; this is the listeners and
 * the indicator. Legacy wraps `MojProfil` in it and nothing else, which is the right place: the profile
 * is the one customer screen whose contents change while it is open — an order moves to *on the way*, a
 * coupon is spent, points land — and it is the one a customer returns to expecting news.
 *
 * **Touch only.** A mouse has a reload button and a keyboard shortcut; a thumb has neither.
 *
 * `passive: false` on the move listener is load-bearing. The browser assumes a touch listener will not
 * cancel and starts scrolling immediately; without this the page scrolls *and* the indicator follows,
 * and the gesture feels like a fault rather than a feature.
 */
export function PullToRefresh({
  onRefresh,
  children,
  enabled = true,
}: {
  onRefresh: () => Promise<unknown> | void
  children: ReactNode
  enabled?: boolean
}) {
  const [pull, setPull] = useState<PullState>(NOT_PULLING)
  const [refreshing, setRefreshing] = useState(false)

  // The listeners are registered once per mount, so the callback reaches them through a ref rather than
  // a closure — `onRefresh` is a new function every render, and putting it in the deps would tear the
  // listeners down and rebuild them mid-drag.
  const refresh = useRef(onRefresh)
  useEffect(() => {
    refresh.current = onRefresh
  }, [onRefresh])

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined

    // The gesture's own state lives here, in the effect, and React state only mirrors it for rendering.
    // A ref written during render is what the first version did, and it is a rule violation for a
    // reason: the value a listener reads and the value the screen shows would be from different renders.
    let state = NOT_PULLING
    let busy = false

    function start(event: TouchEvent) {
      if (busy) return
      const touch = event.touches[0]
      if (touch === undefined) return
      state = onTouchStart(touch.clientY, window.scrollY)
      setPull(state)
    }

    function move(event: TouchEvent) {
      if (busy) return
      const touch = event.touches[0]
      if (touch === undefined) return
      state = onTouchMove(state, touch.clientY)
      setPull(state)
      // Only once the pull is real: cancelling every touch at the top of the page would stop the
      // customer scrolling down at all.
      if (state.distance > 4 && event.cancelable) event.preventDefault()
    }

    function end() {
      const released = state
      state = NOT_PULLING
      setPull(NOT_PULLING)
      if (!shouldRefresh(released) || busy) return
      busy = true
      setRefreshing(true)
      void Promise.resolve(refresh.current()).finally(() => {
        busy = false
        setRefreshing(false)
      })
    }

    window.addEventListener('touchstart', start, { passive: true })
    window.addEventListener('touchmove', move, { passive: false })
    window.addEventListener('touchend', end)
    window.addEventListener('touchcancel', end)
    return () => {
      window.removeEventListener('touchstart', start)
      window.removeEventListener('touchmove', move)
      window.removeEventListener('touchend', end)
      window.removeEventListener('touchcancel', end)
    }
  }, [enabled])

  const offset = refreshing ? PULL_THRESHOLD : pull.distance
  const armed = shouldRefresh(pull)

  return (
    <div data-pull-to-refresh={refreshing ? 'refreshing' : armed ? 'armed' : 'idle'}>
      <div
        aria-hidden={offset === 0}
        className="flex items-center justify-center overflow-hidden transition-[height] duration-200"
        style={{ height: offset }}
      >
        {refreshing ? (
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        ) : (
          <RotateCw
            className="w-5 h-5 text-muted-foreground transition-transform"
            style={{ transform: `rotate(${pullProgress(pull) * 180}deg)` }}
          />
        )}
        <span className="sr-only">{refreshing ? tt('Obnovujem…') : tt('Potiahnutím obnovíte')}</span>
      </div>
      {children}
    </div>
  )
}
